import React, { useState } from 'react';
import { Box, Typography, Card, Container, Grid, Input, Textarea, Button, FormControl, FormLabel, Select, Option, Chip, LinearProgress } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import axios from 'axios';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: '',
    aiInterest: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectTypes = [
    'Web Application',
    'Mobile App',
    'AI/ML System',
    'E-commerce Platform',
    'Data Analytics',
    'IoT Solution',
    'API Service',
    'Enterprise Software',
    'Process Automation',
    'Business Intelligence'
  ];

  const budgetRanges = [
    'Under $10K',
    '$10K - $25K',
    '$25K - $50K',
    '$50K - $100K',
    '$100K - $250K',
    '$250K+'
  ];

  const timelineOptions = [
    '1-2 months',
    '3-4 months',
    '5-6 months',
    '6+ months',
    'Ongoing project'
  ];

  const aiOptions = [
    'Intelligent Automation',
    'Predictive Analytics',
    'Natural Language Processing',
    'Computer Vision',
    'Recommendation Systems',
    'Process Optimization',
    'Smart Monitoring',
    'Decision Support'
  ];

  const contactInfo = [
    {
      icon: <AutoAwesomeIcon />,
      title: 'AI-Powered Analysis',
      value: 'Instant Project Insights',
      description: 'Get immediate cost, timeline, and tech recommendations'
    },
    {
      icon: <LocationOnIcon />,
      title: 'Global Reach',
      value: 'Remote-First Team',
      description: 'Serving clients worldwide with intelligent solutions'
    }
  ];

  const generateProjectAnalysis = async () => {
    if (!formData.message.trim()) {
      toast.error('Please describe your project details');
      return;
    }
    
    if (!formData.email.trim()) {
      toast.error('Please provide your email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(
        '/api/analyze-project',
        {
          description: formData.message,
          projectType: formData.projectType,
          technologies: formData.aiInterest,
          timeline: formData.timeline,
          budget: formData.budget,
          email: formData.email,
          name: formData.name || formData.email
        }
      );
      
      setIsSubmitting(false);
      toast.success(response.data.message);
      
      // Clear form after successful submission
      setFormData({ 
        name: '', 
        email: '', 
        company: '', 
        projectType: '', 
        budget: '', 
        timeline: '', 
        message: '', 
        aiInterest: [] 
      });
    } catch (error) {
      console.error('Submission error:', error);
      setIsSubmitting(false);
      
      // Handle different error types
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'] || 60;
        toast.error(`Rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`);
      } else if (error.response?.status === 413) {
        toast.error('Project description is too long. Please shorten it and try again.');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || 'Invalid request. Please check your input.');
      } else {
        toast.error('Something went wrong. Please try again or contact us directly.');
      }
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    generateProjectAnalysis();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Box sx={{ py: 12, minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography 
              level="h1" 
              sx={{ 
                mb: 4,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 50%, #00A86B 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Get Your Project Estimate Now
            </Typography>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'text.secondary',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400
              }}
            >
              Our AI analyzes your project requirements and provides instant estimates for cost, timeline, and technology recommendations. Start your intelligent solution today.
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid xs={12} lg={8}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Card
                sx={{
                  p: 6,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                  <AutoAwesomeIcon sx={{ color: '#2E5BBA', fontSize: '2rem' }} />
                  <Typography level="h2" sx={{ color: 'text.primary' }}>
                    Get Your AI Project Estimate
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid xs={12} sm={6}>
                      <FormControl required>
                        <FormLabel sx={{ color: 'text.primary', mb: 1 }}>Full Name</FormLabel>
                        <Input
                          placeholder="Your full name"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                            },
                            '&:focus-within': {
                              border: '1px solid #2E5BBA',
                            }
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <FormControl required>
                        <FormLabel sx={{ color: 'text.primary', mb: 1 }}>Email Address</FormLabel>
                        <Input
                          type="email"
                          placeholder="your.email@company.com"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                            },
                            '&:focus-within': {
                              border: '1px solid #2E5BBA',
                            }
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <FormControl>
                        <FormLabel sx={{ color: 'text.primary', mb: 1 }}>Company</FormLabel>
                        <Input
                          placeholder="Your company name"
                          value={formData.company}
                          onChange={(e) => handleInputChange('company', e.target.value)}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                            },
                            '&:focus-within': {
                              border: '1px solid #2E5BBA',
                            }
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={4}>
                      <FormControl required>
                        <FormLabel sx={{ color: 'text.primary', mb: 1 }}>Project Type</FormLabel>
                        <Select
                          placeholder="Select project type"
                          value={formData.projectType}
                          onChange={(e, value) => handleInputChange('projectType', value)}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                            }
                          }}
                        >
                          {projectTypes.map((type) => (
                            <Option key={type} value={type}>{type}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={4}>
                      <FormControl>
                        <FormLabel sx={{ color: 'text.primary', mb: 1 }}>Budget Range</FormLabel>
                        <Select
                          placeholder="Select budget"
                          value={formData.budget}
                          onChange={(e, value) => handleInputChange('budget', value)}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                            }
                          }}
                        >
                          {budgetRanges.map((range) => (
                            <Option key={range} value={range}>{range}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={4}>
                      <FormControl>
                        <FormLabel sx={{ color: 'text.primary', mb: 1 }}>Timeline</FormLabel>
                        <Select
                          placeholder="Select timeline"
                          value={formData.timeline}
                          onChange={(e, value) => handleInputChange('timeline', value)}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                            }
                          }}
                        >
                          {timelineOptions.map((option) => (
                            <Option key={option} value={option}>{option}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <FormControl>
                        <FormLabel sx={{ color: 'text.primary', mb: 1 }}>AI Features of Interest</FormLabel>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {aiOptions.map((option) => (
                            <Chip
                              key={option}
                              variant={formData.aiInterest.includes(option) ? 'solid' : 'outlined'}
                              onClick={() => {
                                const newInterests = formData.aiInterest.includes(option)
                                  ? formData.aiInterest.filter(item => item !== option)
                                  : [...formData.aiInterest, option];
                                handleInputChange('aiInterest', newInterests);
                              }}
                              sx={{
                                cursor: 'pointer',
                                ...(formData.aiInterest.includes(option) ? {
                                  background: '#2E5BBA',
                                  color: 'white'
                                } : {
                                  borderColor: 'rgba(255, 255, 255, 0.2)',
                                  color: 'text.secondary',
                                  '&:hover': {
                                    borderColor: '#2E5BBA',
                                    color: '#2E5BBA'
                                  }
                                })
                              }}
                            >
                              {option}
                            </Chip>
                          ))}
                        </Box>
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <FormControl required>
                        <FormLabel sx={{ color: 'text.primary', mb: 1 }}>Project Details</FormLabel>
                        <Textarea
                          placeholder="Tell us about your project, goals, and any specific requirements..."
                          minRows={4}
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                            },
                            '&:focus-within': {
                              border: '1px solid #2E5BBA',
                            }
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <Button
                        type="submit"
                        size="lg"
                        disabled={isSubmitting || !formData.message.trim() || !formData.email.trim()}
                        endDecorator={<AutoAwesomeIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
                          color: 'white',
                          px: 4,
                          py: 2,
                          fontWeight: 600,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 10px 30px rgba(46, 91, 186, 0.3)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {isSubmitting ? 'Submitting...' : 'Get Free AI Project Analysis'}
                      </Button>
                      
                      {isSubmitting && (
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress />
                          <Typography level="body2" sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                            Analyzing your project and sending results to your email...
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </form>
              </Card>
            </motion.div>
          </Grid>

          {/* Contact Information */}
          <Grid xs={12} lg={4}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography level="h3" sx={{ color: 'text.primary', mb: 2 }}>
                  Why Choose Our AI Analysis?
                </Typography>
                
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                  >
                    <Card
                      sx={{
                        p: 4,
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          transform: 'translateY(-5px)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            flexShrink: 0,
                            '& svg': { fontSize: '1.5rem' }
                          }}
                        >
                          {info.icon}
                        </Box>
                        <Box>
                          <Typography 
                            level="h4" 
                            sx={{ 
                              color: 'text.primary',
                              mb: 1,
                              fontWeight: 600
                            }}
                          >
                            {info.title}
                          </Typography>
                          <Typography 
                            level="body1" 
                            sx={{ 
                              color: 'text.primary',
                              mb: 1,
                              fontWeight: 500
                            }}
                          >
                            {info.value}
                          </Typography>
                          <Typography 
                            level="body2" 
                            sx={{ 
                              color: 'text.secondary'
                            }}
                          >
                            {info.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                ))}

                {/* Quick Response Promise */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <Card
                    sx={{
                      p: 4,
                      background: 'linear-gradient(135deg, rgba(46, 91, 186, 0.1) 0%, rgba(0, 168, 107, 0.1) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(46, 91, 186, 0.2)',
                      borderRadius: 3,
                      textAlign: 'center'
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: '2rem', color: '#2E5BBA', mb: 2 }} />
                    <Typography level="h4" sx={{ color: 'text.primary', mb: 2 }}>
                      AI-Powered Response
                    </Typography>
                    <Typography level="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      Our intelligent system analyzes your project requirements and provides 
                      instant insights on solutions, timelines, and ROI potential.
                    </Typography>
                  </Card>
                </motion.div>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactPage;